<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";

import type { FileSource, MomentId, PendingFile } from "@/types/album";
import { MOMENT_LABELS } from "@/types/album";

import BaseSheet from "@/components/base/BaseSheet.vue";
import { createId } from "@/lib/id";
import { describeRejection, detectKind, isLikelyHeic, validateFiles } from "@/lib/validation";
import { useAlbumStore } from "@/stores/album";
import { useGuestIdentityStore } from "@/stores/guestIdentity";
import { useNotificationsStore } from "@/stores/notifications";

/**
 * Port de `#uploadDialog` (design-reference/index.html), bâti sur `BaseSheet`.
 *
 * Correctif par rapport à l'original : la revue de sélection propose
 * désormais « Ajouter d'autres fichiers » (append) en plus de « Remplacer »,
 * et « Ajouter d'autres souvenirs » après un envoi rouvre directement le
 * sélecteur au lieu de renvoyer l'invité à l'étape d'accueil vide.
 */

/**
 * Il n'y a pas d'étape d'accueil : la feuille ne s'ouvre qu'une fois des
 * fichiers en main, et se referme dès qu'il n'en reste plus. Un écran « choisir
 * des fichiers » à l'intérieur signalerait un trou dans le parcours.
 */
type Step = "review" | "success";
/**
 * `swap` remplace une vignette précise : le geste « changer celle-ci » vit sur
 * la photo concernée, à côté de sa croix, plutôt que dans une commande globale
 * qui obligerait à tout refaire pour corriger un seul cliché.
 */
type PickerMode = "append" | "replace" | "swap";

const props = defineProps<{
  /** Ouverture pilotée par le parent. */
  open: boolean;
  /** Fichiers pré-sélectionnés (prise de vue, glisser-déposer) : démarre en revue. */
  initialFiles?: File[];
  /** Provenance de la sélection courante, qui décide du geste de complément. */
  source?: FileSource;
}>();

const emit = defineEmits<{
  "update:open": [value: boolean];
  /** Le parent doit faire défiler jusqu'à l'album et mettre en avant cette carte. */
  highlight: [id: string];
}>();

/**
 * Le HEIC est le format natif des iPhone et seul Safari sait l'afficher. Le
 * fichier reste accepté et conservé intact ; on explique simplement pourquoi
 * l'aperçu manque, avec le réglage qui règle le problème pour de bon.
 */
const HEIC_ADVICE =
  "Cette photo est au format HEIC, que votre navigateur ne sait pas afficher. Elle est bien enregistrée. Pour éviter le souci : Réglages › Appareil photo › Formats › « Plus compatible ».";

const guestIdentity = useGuestIdentityStore();
const notifications = useNotificationsStore();
const album = useAlbumStore();

const step = ref<Step>("review");
const pending = ref<PendingFile[]>([]);
const pendingMode = ref<PickerMode>("replace");
/** Vignette visée par un remplacement en cours. */
const swapTargetId = ref<string | null>(null);
const failedPreviews = ref<Set<string>>(new Set());
const fileInput = ref<HTMLInputElement | null>(null);
const cameraInput = ref<HTMLInputElement | null>(null);
const successStep = ref<HTMLElement | null>(null);

const guestName = ref(guestIdentity.name);
const moment = ref<MomentId | "">(guestIdentity.lastMoment ?? "");
const caption = ref("");

const isUploading = ref(false);
const lastAddedCount = ref(0);
const newestId = ref<string | null>(null);

const momentOptions = (Object.keys(MOMENT_LABELS) as MomentId[]).map((id) => ({
  id,
  label: MOMENT_LABELS[id],
}));

const successMessage = computed(() =>
  lastAddedCount.value > 1
    ? `${lastAddedCount.value} souvenirs sont enregistrés sur cet appareil.`
    : "Votre souvenir est enregistré sur cet appareil.",
);

watch(guestName, (value) => guestIdentity.setName(value));
// Le choix n'est mémorisé qu'une fois réellement fait : réécrire une valeur
// vide effacerait le moment retenu de la visite précédente.
watch(moment, (value) => {
  if (value) guestIdentity.setLastMoment(value);
});

function revokePending(entries: PendingFile[]): void {
  entries.forEach((entry) => URL.revokeObjectURL(entry.previewUrl));
}

/** Valide et intègre de nouveaux fichiers dans la sélection courante. */
function ingestFiles(files: File[], mode: PickerMode): void {
  const baseline =
    mode === "append"
      ? pending.value.map((entry) => entry.file)
      : mode === "swap"
        ? pending.value
            .filter((entry) => entry.id !== swapTargetId.value)
            .map((entry) => entry.file)
        : [];
  const result = validateFiles(files, baseline);

  result.rejected.forEach(({ file, reason }) => {
    notifications.notify(describeRejection(reason, file), "error");
  });

  if (!result.accepted.length) return;

  const created: PendingFile[] = [];
  for (const file of result.accepted) {
    const kind = detectKind(file);
    // `validateFiles` a déjà écarté les types inconnus : ce garde-fou ne
    // devrait jamais se déclencher, il évite seulement une assertion `!`.
    if (!kind) continue;
    created.push({ id: createId("pending"), file, kind, previewUrl: URL.createObjectURL(file) });
  }
  if (!created.length) return;

  if (mode === "swap") {
    const index = pending.value.findIndex((entry) => entry.id === swapTargetId.value);
    swapTargetId.value = null;
    if (index >= 0) {
      const [replaced] = pending.value.splice(index, 1, created[0]!);
      if (replaced) {
        URL.revokeObjectURL(replaced.previewUrl);
        failedPreviews.value.delete(replaced.id);
      }
      // `splice` mute le tableau : la copie force le rendu à se rafraîchir.
      pending.value = [...pending.value];
      return;
    }
    pending.value = [...pending.value, ...created];
  } else if (mode === "replace") {
    revokePending(pending.value);
    pending.value = created;
  } else {
    pending.value = [...pending.value, ...created];
  }

  step.value = "review";
}

function openPicker(mode: PickerMode): void {
  pendingMode.value = mode;
  const input = fileInput.value;
  if (!input) return;
  // Sans cette remise à zéro, re-choisir exactement les mêmes fichiers ne
  // redéclenche pas `change`.
  input.value = "";
  input.click();
}

/**
 * Prise de vue depuis la revue, pour compléter une sélection sans repartir de
 * l'album. Comme sur l'accroche, l'`accept` ne liste qu'`image/*` : au-delà,
 * iOS ignore `capture` et ouvre la photothèque.
 */
function openCamera(mode: PickerMode): void {
  pendingMode.value = mode;
  const input = cameraInput.value;
  if (!input) return;
  input.value = "";
  input.click();
}

const fromCamera = computed(() => props.source === "camera");

/**
 * Compléter la sélection reprend le geste qui l'a ouverte. Renvoyer vers la
 * photothèque quelqu'un qui vient de photographier lui ferait chercher, parmi
 * des milliers de clichés, celui qu'il vient de prendre.
 */
/** Ouvre la source d'origine pour remplacer une vignette précise. */
function swapPending(id: string): void {
  swapTargetId.value = id;
  if (fromCamera.value) {
    openCamera("swap");
    return;
  }
  openPicker("swap");
}

function addMore(): void {
  if (fromCamera.value) {
    openCamera("append");
    return;
  }
  openPicker("append");
}

/**
 * Recommencer rouvre la source d'origine en mode « remplacer ». On ne vide pas
 * la sélection d'abord : renoncer au sélecteur laisserait sinon l'invité devant
 * un écran vide, alors qu'il avait déjà choisi quelque chose.
 */
function restartSelection(): void {
  if (fromCamera.value) {
    openCamera("replace");
    return;
  }
  openPicker("replace");
}

function onCameraShot(): void {
  const input = cameraInput.value;
  if (!input) return;
  const files = [...(input.files ?? [])];
  input.value = "";
  if (files.length) ingestFiles(files, pendingMode.value);
}

// --- Glisser-déposer -------------------------------------------------------

const isDragging = ref(false);
/** `dragenter`/`dragleave` remontent aussi des enfants : on compte les passages. */
let dragDepth = 0;

function dragHasFiles(event: DragEvent): boolean {
  return [...(event.dataTransfer?.types ?? [])].includes("Files");
}

function onDragEnter(event: DragEvent): void {
  if (!dragHasFiles(event)) return;
  dragDepth += 1;
  isDragging.value = true;
}

function onDragOver(event: DragEvent): void {
  if (!dragHasFiles(event)) return;
  // Sans ce `preventDefault`, le navigateur refuse le dépôt et ouvre le
  // fichier dans l'onglet à la place.
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
}

function onDragLeave(): void {
  dragDepth = Math.max(0, dragDepth - 1);
  if (dragDepth === 0) isDragging.value = false;
}

/** Déposer sur la revue ajoute à la sélection ; déposer sur l'accueil la crée. */
function onDrop(event: DragEvent, mode: PickerMode): void {
  event.preventDefault();
  dragDepth = 0;
  isDragging.value = false;
  const files = [...(event.dataTransfer?.files ?? [])];
  if (files.length) ingestFiles(files, mode);
}

function onFilesChosen(): void {
  const input = fileInput.value;
  if (!input) return;
  const files = [...(input.files ?? [])];
  input.value = "";
  if (!files.length) return;
  ingestFiles(files, pendingMode.value);
}

/**
 * Un aperçu qui échoue signale un fichier que le navigateur ne sait pas
 * décoder — typiquement un HEIC d'iPhone hors Safari, ou un fichier tronqué.
 * On le dit explicitement plutôt que de laisser une vignette grise muette.
 */
function onPreviewError(id: string): void {
  failedPreviews.value = new Set(failedPreviews.value).add(id);

  const entry = pending.value.find((candidate) => candidate.id === id);
  if (entry && isLikelyHeic(entry.file)) {
    notifications.notify(HEIC_ADVICE, "error");
  }
}

function removePending(id: string): void {
  const target = pending.value.find((entry) => entry.id === id);
  if (!target) return;
  URL.revokeObjectURL(target.previewUrl);
  pending.value = pending.value.filter((entry) => entry.id !== id);
  if (failedPreviews.value.has(id)) {
    const next = new Set(failedPreviews.value);
    next.delete(id);
    failedPreviews.value = next;
  }
  if (!pending.value.length) {
    emit("update:open", false);
  }
}

function resetTransient(): void {
  revokePending(pending.value);
  pending.value = [];
  failedPreviews.value = new Set();
  caption.value = "";
  step.value = "review";
}

async function submit(): Promise<void> {
  if (!pending.value.length || isUploading.value) return;
  isUploading.value = true;
  try {
    const created = await album.addGuestMedia({
      files: pending.value.map((entry) => entry.file),
      author: guestName.value,
      moment: moment.value || undefined,
      caption: caption.value,
    });
    lastAddedCount.value = created.length;
    newestId.value = created[0]?.id ?? null;
    revokePending(pending.value);
    pending.value = [];
    failedPreviews.value = new Set();
    caption.value = "";
    step.value = "success";
    // Le bouton d'envoi qui portait le focus vient d'être démonté : sans cette
    // reprise, le focus retombe sur `<body>`, le succès n'est pas annoncé aux
    // lecteurs d'écran et la tabulation suivante repart du début de la feuille.
    await nextTick();
    successStep.value?.focus();
  } catch (cause) {
    console.error("[upload] l'ajout à la galerie a échoué", cause);
    notifications.notify("L’ajout a échoué. Réessayez.", "error");
  } finally {
    isUploading.value = false;
  }
}

/**
 * Depuis l'écran de succès, on rouvre directement la source. L'écran de succès
 * reste affiché derrière le sélecteur du système : renoncer au choix ne laisse
 * donc jamais l'invité devant une feuille vide.
 */
function handleAddMore(): void {
  restartSelection();
}

function handleViewInGallery(): void {
  emit("update:open", false);
  if (newestId.value) emit("highlight", newestId.value);
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return;
    resetTransient();
    guestName.value = guestIdentity.name;
    moment.value = guestIdentity.lastMoment ?? "";
    if (props.initialFiles && props.initialFiles.length > 0) {
      ingestFiles(props.initialFiles, "replace");
    }
    // Tous les fichiers rejetés (mauvais type, trop lourds) : la notification
    // l'explique déjà, garder une feuille vide ouverte n'ajouterait rien.
    if (!pending.value.length) emit("update:open", false);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  revokePending(pending.value);
});
</script>

<template>
  <BaseSheet
    :open="open"
    kicker="Pour l’album de Sarah &amp; Quentin"
    title="Ajouter des souvenirs"
    panel-class="upload-sheet"
    data-testid="upload-sheet"
    @update:open="(value) => emit('update:open', value)"
  >
    <input
      ref="fileInput"
      type="file"
      accept="image/*,video/*"
      multiple
      class="u-sr-only"
      data-testid="file-input"
      @change="onFilesChosen"
    />

    <input
      ref="cameraInput"
      type="file"
      accept="image/*"
      capture="environment"
      class="u-sr-only"
      data-testid="sheet-camera-input"
      @change="onCameraShot"
    />

    <section v-if="step === 'review'" class="upload-step" data-testid="upload-step-review">
      <div
        class="selection-grid"
        :class="{ 'is-dragging': isDragging }"
        data-testid="selection-grid"
        @dragenter="onDragEnter"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @drop="(event) => onDrop(event, 'append')"
      >
        <div
          v-for="entry in pending"
          :key="entry.id"
          class="selection-item"
          :class="{ 'selection-item--video': entry.kind === 'video' }"
          data-testid="selection-item"
          :data-file-name="entry.file.name"
        >
          <template v-if="!failedPreviews.has(entry.id)">
            <video
              v-if="entry.kind === 'video'"
              :src="entry.previewUrl"
              muted
              preload="metadata"
              playsinline
              @error="onPreviewError(entry.id)"
            ></video>
            <img v-else :src="entry.previewUrl" alt="" @error="onPreviewError(entry.id)" />
          </template>
          <div
            v-else
            class="selection-item__fallback"
            :title="`${entry.file.name} ne peut pas être affiché par votre navigateur.`"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 8v5" />
              <path d="M12 16.5h.01" />
              <path
                d="M10.3 3.8 2.4 17.4A1.9 1.9 0 0 0 4 20.3h16a1.9 1.9 0 0 0 1.6-2.9L13.7 3.8a1.9 1.9 0 0 0-3.4 0Z"
              />
            </svg>
            <span class="u-sr-only">Aperçu indisponible pour {{ entry.file.name }}</span>
          </div>
          <button
            type="button"
            class="selection-item__swap"
            data-testid="swap-file"
            :aria-label="`Remplacer ${entry.file.name}`"
            @click="swapPending(entry.id)"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20 11a8 8 0 1 0-.6 4"></path>
              <path d="M20 4v7h-7"></path>
            </svg>
          </button>
          <button
            type="button"
            class="selection-item__remove"
            data-testid="remove-file"
            :aria-label="`Retirer ${entry.file.name}`"
            @click="removePending(entry.id)"
          >
            ×
          </button>
        </div>

        <!-- Case vide à la suite des vignettes : ajouter une photo se lit comme
             remplir l'emplacement suivant de la planche, pas comme actionner une
             commande posée au-dessus. -->
        <button
          type="button"
          class="selection-add"
          data-testid="append-files"
          :aria-label="fromCamera ? 'Prendre une autre photo' : 'Ajouter d’autres photos'"
          @click="addMore"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 6v12M6 12h12"></path>
          </svg>
        </button>
      </div>

      <p class="form-note">Rien n’est obligatoire.</p>

      <div class="form-grid">
        <label>
          <span>Votre prénom</span>
          <input
            v-model="guestName"
            type="text"
            placeholder="Ex. Thomas"
            maxlength="30"
            data-testid="guest-name"
          />
        </label>
        <label>
          <span>Moment de la journée</span>
          <select v-model="moment" data-testid="moment-select">
            <option value="">Sans précision</option>
            <option v-for="option in momentOptions" :key="option.id" :value="option.id">
              {{ option.label }}
            </option>
          </select>
        </label>
      </div>
      <label class="caption-field">
        <span>Un mot</span>
        <textarea
          v-model="caption"
          placeholder="Racontez ce moment en quelques mots…"
          maxlength="140"
          data-testid="caption-input"
        ></textarea>
        <span v-if="caption.length > 100" class="caption-field__counter"
          >{{ caption.length }} / 140</span
        >
      </label>
      <button
        type="button"
        class="upload-submit"
        :class="{ 'is-uploading': isUploading }"
        :disabled="isUploading || !pending.length"
        data-testid="submit-upload"
        @click="() => void submit()"
      >
        <span>{{ isUploading ? "Ajout en cours…" : "Ajouter à la galerie" }}</span>
      </button>
    </section>

    <section
      v-else
      ref="successStep"
      class="upload-step upload-success"
      tabindex="-1"
      data-testid="upload-step-success"
    >
      <div class="success-mark">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m5 12 4 4L19 6"></path>
        </svg>
      </div>
      <h2>Merci d’avoir partagé<br />votre regard.</h2>
      <p data-testid="success-message">{{ successMessage }}</p>
      <button
        type="button"
        class="view-upload"
        data-testid="view-in-gallery"
        @click="handleViewInGallery"
        >Voir dans la galerie</button
      >
      <button type="button" class="add-more" data-testid="add-more" @click="handleAddMore"
        >Ajouter d’autres souvenirs</button
      >
    </section>
  </BaseSheet>
</template>

<style scoped>
.upload-step {
  animation: var(--animate-step-in);
}

/* Les enfants avaleraient `dragleave` sans cette neutralisation. */
.selection-grid.is-dragging {
  outline: 2px dashed var(--color-cta);
  outline-offset: 5px;
  border-radius: 12px;
}

/* Case vide de la planche, au même gabarit que les vignettes : elle appelle le
   geste sans occuper de ligne à elle. */
.selection-add {
  display: grid;
  aspect-ratio: 1;
  place-items: center;
  padding: 0;
  color: var(--color-cta);
  border: 2px dashed rgb(82 102 90 / 0.32);
  border-radius: 3px;
  background: rgb(157 167 159 / 0.09);
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;
}

.selection-add:hover,
.selection-add:focus-visible {
  border-color: var(--color-cta);
  background: rgb(104 122 92 / 0.12);
}

.selection-add svg {
  width: 26px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-width: 1.6;
}

/* Symétrique de la croix, dans le coin opposé : retirer et remplacer sont deux
   corrections de même niveau, toutes deux posées sur la photo concernée. */
.selection-item__swap {
  position: absolute;
  top: 5px;
  left: 5px;
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  padding: 0;
  color: white;
  border: 1px solid rgb(255 255 255 / 0.3);
  border-radius: 50%;
  background: rgb(29 37 32 / 0.72);
}

.selection-item__swap svg {
  width: 13px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}

.selection-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}

/* Chaque fichier retenu est déjà un petit polaroïd : cadre ivoire et ombre
   portée, comme dans la galerie où il finira. */
.selection-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 3px;
  background: var(--color-ivory);
  box-shadow: var(--shadow-polaroid);
  overflow: hidden;
}

.selection-item img,
.selection-item video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.selection-item--video {
  background: linear-gradient(135deg, #9c6b60, #3e5247);
}

.selection-item__fallback {
  display: grid;
  width: 100%;
  height: 100%;
  place-items: center;
  color: white;
}

.selection-item__fallback svg {
  width: 22px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.5;
}

.selection-item__remove {
  position: absolute;
  top: 5px;
  right: 5px;
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  padding: 0;
  color: white;
  border: 1px solid rgb(255 255 255 / 0.3);
  border-radius: 50%;
  background: rgb(29 37 32 / 0.72);
  font-size: 15px;
}

.form-note {
  margin: 0 0 8px 2px;
  color: var(--color-muted);
  font-size: 13px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

/* Casse normale : les capitales espacées étaient ce qui donnait le plus le
   ton administratif, et rien d'autre sur le site n'en emploie. */
.form-grid label > span,
.caption-field > span {
  display: block;
  margin: 0 0 6px 2px;
  color: var(--color-muted);
  font-size: 13px;
  font-weight: 500;
}

.caption-field > span i {
  font-style: italic;
  opacity: 0.85;
}

.form-grid input,
.form-grid select,
.caption-field textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-line);
  border-radius: 10px;
  outline: none;
  background: var(--color-paper);
  font-size: 14px;
}

.form-grid input:focus,
.form-grid select:focus,
.caption-field textarea:focus {
  border-color: var(--color-rose);
}

.caption-field {
  display: block;
  margin-top: 14px;
}

.caption-field textarea {
  min-height: 72px;
  resize: none;
}

.caption-field__counter {
  display: block;
  margin: 5px 2px 0;
  color: var(--color-muted);
  font-size: 12px;
  text-align: right;
  text-transform: none;
}

.upload-submit {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 15px;
  padding: 13px 18px;
  color: white;
  border: 0;
  border-radius: 12px;
  background: var(--color-olive);
  font-family: var(--font-script);
  font-size: 24px;
}

.upload-submit.is-uploading {
  cursor: wait;
  opacity: 0.68;
}

.upload-submit:disabled:not(.is-uploading) {
  cursor: not-allowed;
  opacity: 0.5;
}

.upload-success {
  padding: 12px 0 20px;
  text-align: center;
}

/* Le focus est posé par le script pour que le succès soit annoncé aux lecteurs
   d'écran. Ce n'est pas une commande : le contour du navigateur n'a rien à
   entourer ici. */
.upload-success:focus {
  outline: none;
}

.success-mark {
  display: grid;
  width: 66px;
  height: 66px;
  margin: 0 auto 20px;
  place-items: center;
  color: white;
  border-radius: 50%;
  background: var(--color-olive);
  box-shadow: 0 0 0 11px rgb(104 113 60 / 0.1);
}

.success-mark svg {
  width: 27px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.6;
}

.upload-success h2 {
  margin: 11px 0 12px;
  font-family: var(--font-serif);
  font-size: 36px;
  font-weight: 400;
  line-height: 1.05;
  -webkit-text-stroke: 0.18px currentColor;
}

.upload-success p {
  color: var(--color-muted);
  font-size: 13px;
}

.view-upload,
.add-more {
  width: 100%;
  padding: 11px;
  border-radius: 11px;
  font-family: var(--font-script);
  font-size: 22px;
}

.view-upload {
  margin-top: 18px;
  color: white;
  border: 0;
  background: linear-gradient(135deg, var(--color-cta), var(--color-cta-dark));
}

.add-more {
  margin-top: 8px;
  color: var(--color-ink);
  border: 1px solid var(--color-line);
  background: transparent;
}
</style>
